import { Box } from "@mui/material";
import { useMemo } from "react";

import { GaiaMap } from "~/modules/map/components/GaiaMap";
import GaiaMapDataLayer from "~/modules/map/components/GaiaMapDataLayer";
import { GaiaMapStylePlannerArea } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleAreaFillSolid";
import { GaiaMapStyleAreaFillWithDashedLines } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleAreaFillWithDashedLines";
import { GaiaMapStyleCircle } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleCircle";
import { GaiaMapStyleLine } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleLine";
import { LAYER_DEFAULTS } from "~/modules/map/components/GaiaMapStyleComposites/layerDefaults";
import { loadMapShapeIcons } from "~/modules/map/icons";
import { MAP_MARGIN, MAP_PANEL_WIDTH_LEGEND } from "~/modules/map/mapConstants";

import { ProjectDeliverablesMap_FeatureFragment } from "../../fragments.generated";
import { ProjectDeliverablesMapLegend } from "../ProjectDeliverablesMapLegend";
import { useMapLegendData } from "./hooks/useMapLegendData";
import { usePreparedGeoJson } from "./hooks/usePreparedGeoJson";

const MAP_HEIGHT = 740;

const POINTS_SOURCE_ID = "points";
const LINES_SOURCE_ID = "lines";
const POLYGONS_SOURCE_ID = "polygons";
const PLANNER_AREAS_SOURCE_ID = "plannerAreas";

type Props = {
    features: ProjectDeliverablesMap_FeatureFragment[];
};

export function ProjectDeliverablesMapView({ features }: Props) {
    const { points, lines, polygons, plannerAreas, bounds } = usePreparedGeoJson(features);
    const { species, pointFunctionTypes, lineFunctionTypes, polygonFunctionTypes } = useMapLegendData(features);
    const polygonColors = useMemo(() => [...new Set(polygons.features.map(feature => feature.properties.color))], [polygons]);

    return (
        <Box sx={{ position: "relative", height: MAP_HEIGHT }}>
            <GaiaMap
                scrollOnBoundsChange
                bounds={bounds}
                padding={{
                    left: MAP_PANEL_WIDTH_LEGEND + MAP_MARGIN,
                    top: MAP_MARGIN,
                    bottom: MAP_MARGIN,
                    right: MAP_MARGIN,
                }}
                interactive={false}
                sx={{ height: "100%", width: "100%" }}
                onMapLoaded={async map => {
                    await loadMapShapeIcons(map);
                }}
            >
                <GaiaMapDataLayer id={POINTS_SOURCE_ID} data={points} />
                <GaiaMapDataLayer id={LINES_SOURCE_ID} data={lines} />
                <GaiaMapDataLayer id={POLYGONS_SOURCE_ID} data={polygons} />
                <GaiaMapDataLayer id={PLANNER_AREAS_SOURCE_ID} data={plannerAreas} />

                <GaiaMapStylePlannerArea id={PLANNER_AREAS_SOURCE_ID} source={PLANNER_AREAS_SOURCE_ID} />
                <GaiaMapStyleAreaFillWithDashedLines
                    id={`${POLYGONS_SOURCE_ID}_fill`}
                    source={POLYGONS_SOURCE_ID}
                    color={["get", "color"]}
                    pattern={["get", "fillPatternId"]}
                    colors={polygonColors}
                />
                <GaiaMapStyleLine
                    id={`${LINES_SOURCE_ID}_line`}
                    source={LINES_SOURCE_ID}
                    color={["get", "color"]}
                    width={3}
                    dashArray={[1.5, 1.5]}
                />
                <GaiaMapStyleCircle
                    id={`${POINTS_SOURCE_ID}_shape`}
                    source={POINTS_SOURCE_ID}
                    color={["get", "color"]}
                    radius={14}
                    icon={{ image: ["get", "iconId"], size: 0.5, color: LAYER_DEFAULTS.iconColor.onMarker }}
                />
            </GaiaMap>

            <ProjectDeliverablesMapLegend
                species={species}
                pointFunctionTypes={pointFunctionTypes}
                lineFunctionTypes={lineFunctionTypes}
                polygonFunctionTypes={polygonFunctionTypes}
            />
        </Box>
    );
}
